import { item } from './models/item';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { category } from './models/category';
import { BreadcrumbsComponent } from '../shared/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbsOptions } from '../shared/models/breadcrumbs-model';
import { AlertComponent } from '../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../shared/components/dialogs/confirm/confirm.component';
import { ItemsService } from './services/items/items.service';
import { CategoryService } from './services/categories/category.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { debounceTime } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { HttpClient } from '@angular/common/http';
import { ElementRef } from '@angular/core';

@Component({
  selector: 'app-items',
  templateUrl: './items.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatPaginator,
    MatTableModule,
    TranslateModule,
    FlexLayoutModule,
    BreadcrumbsComponent,
    MatTooltip,
    MatCardModule,
  ],
  styleUrl: './items.component.scss'
})
export class ItemsComponent implements OnInit {
  breadcrumbsData: BreadcrumbsOptions = {
    title: 'general.items',
    buttons: [
      {
        label: 'button.back',
        style: 'blue',
        do: () => {
          this.navigate();
        }
      }
    ]
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('itemFormRef') itemFormViewChild: any;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  itemForm: FormGroup;
  filter: FormControl = new FormControl('');
  isEdit = false;
  displayedColumns: string[] = ['codItem', 'partNumber', 'item', 'category', 'manufacturer', 'action'];
  editItem: item = {
    id: 0,
    description: '',
    category: { id: 0, category: '' },
    partNumber: '',
    manufacturer: '',
    imagePath: null,
    active: true
  };
  pageable: PageEvent = {
    pageIndex: 0,
    pageSize: 5,
    length: 0
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private itemService: ItemsService,
    private categoryService: CategoryService,
    private http: HttpClient
  ) {
    this.itemForm = this.formBuilder.group({
      item: ['', Validators.required],
      category: ['', Validators.required],
      manufacturer: ['', Validators.required],
      partNumber: ['', Validators.required],
    });
  }

  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  items: item[] = [];
  categories: category[] = [];
  dataSource = new MatTableDataSource<item>();
  readonly dialog = inject(MatDialog);

  private normalizeString(value: string): string {
    return value?.trim()?.replace(/\s+/g, ' ') ?? '';
  }

  ngOnInit() {
    const selectedCategory = this.route.snapshot.queryParamMap.get('selectedCategory');
    this.pageChange(this.pageable);
    this.categoryService.listCategories().subscribe(
      categories => {
        this.categories = categories;
        if (selectedCategory) {
          const category = this.categories.find(categoryToFind => categoryToFind.id === +selectedCategory);
          if (category) {
            this.itemForm.patchValue({ category: category });
          }
        }
      }
    );

    this.filter.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
      this.pageable.pageIndex = 0;
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.pageChange(this.pageable);
    });

    if (this.route.snapshot.params['id']) {
      this.isEdit = true;
      this.editItem.id = this.route.snapshot.params['id'];
      this.itemService.getItemById(this.editItem.id).subscribe({
        next: (item) => {
          this.editItem = item;
          const selectedCategory = this.categories.find(category => category.id === item.category.id);
          this.itemForm.patchValue({
            partNumber: item.partNumber,
            item: item.description,
            category: selectedCategory,
            manufacturer: item.manufacturer,
          });
          if (item.id) {
            this.loadItemImage(item.id);
          } else {
            this.editItem.imagePath = null;
          }
        }
      });
    }
  }

  pageChange(event: PageEvent) {
    let filterValue = this.filter.value;
    this.pageable.pageIndex = event.pageIndex;
    this.pageable.pageSize = event.pageSize;
    this.itemService.listItemsPaginatedandFiltered(event, filterValue!).subscribe({
      next: (response) => {
        this.dataSource.data = response.content;
        this.pageable.length = response.totalElements;
      }
    });
  }

  addItem() {
    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (this.selectedFile && this.selectedFile.size > maxSizeBytes) {
      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          title: 'alert.information',
          message: 'item.imageTooLarge'
        }
      });
      this.selectedFile = null;
      this.fileInput.nativeElement.value = '';
      return;
    }

    const formData = new FormData();

    const normalizedDescription = this.normalizeString(this.itemForm.value.item);
    const normalizedManufacturer = this.normalizeString(this.itemForm.value.manufacturer);

    const newItem = {
      description: normalizedDescription,
      category: this.itemForm.value.category,
      manufacturer: normalizedManufacturer,
      partNumber: this.itemForm.value.partNumber,
      active: true,
      imagePath: null
    };

    formData.append('item', JSON.stringify(newItem));

    if (this.selectedFile) {
      formData.append('file', this.selectedFile);
    }

    this.itemService.createItemWithImage(formData).subscribe({
      next: () => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'item.itemAddedSuccess',
            param: { description: newItem.description },
            title: 'alert.information',
          },
        });
        this.clearForm();
        this.pageChange(this.pageable);
      },
      error: (e) => {
        const errorMessage = e.status === 409
          ? 'item.alreadyExists'
          : e.status === 413
            ? 'item.imageTooLarge'
            : 'item.unknownError';

        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: errorMessage,
            param: null,
            title: 'alert.information'
          }
        });
      }
    });
  }

  startUpdate(item: item) {
    this.isEdit = true;
    const selectedCategory = this.categories.find(category => category.id === item.category.id);
    this.itemForm.patchValue({
      partNumber: item.partNumber,
      item: item.description,
      category: selectedCategory,
      manufacturer: item.manufacturer,
    });
    this.editItem = item;

    if (item.id) {
      this.loadItemImage(item.id);
    } else {
      this.editItem.imagePath = null;
    }
    this.selectedFile = null;
  }

  loadItemImage(id: number) {
    if (id) {
      this.itemService.getItemImage(id).subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const reader = new FileReader();
            reader.onload = () => {
              this.editItem.imagePath = reader.result as string;
            };
            reader.readAsDataURL(blob);
          } else {
            this.editItem.imagePath = null;
          }
        },
        error: () => {
          this.editItem.imagePath = null;
        }
      });
    } else {
      this.editItem.imagePath = null;
    }
  }

  updateItem() {
    const normalizedDescription = this.normalizeString(this.itemForm.value.item);
    const normalizedManufacturer = this.normalizeString(this.itemForm.value.manufacturer);

    const updatedItem = {
      id: this.editItem.id,
      description: normalizedDescription,
      category: this.itemForm.value.category,
      manufacturer: normalizedManufacturer,
      active: true,
      partNumber: this.itemForm.value.partNumber
    };

    const formData = new FormData();
    formData.append('item', new Blob([JSON.stringify(updatedItem)], { type: 'application/json' }));

    if (this.selectedFile) {
      formData.append('file', this.selectedFile);
    }

    this.itemService.updateItemWithImage(formData, this.editItem.id!).subscribe({
      next: () => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'item.itemUpdatedSuccess',
            param: { description: updatedItem.description },
            title: 'alert.information',
          },
        });
        this.clearForm();
        this.pageChange(this.pageable);
      },
      error: (e) => {
        const errorMessage = e.status === 409
          ? 'item.alreadyExists'
          : e.status === 413
            ? 'item.imageTooLarge'
            : 'item.unknownError';

        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: errorMessage,
            param: null,
            title: 'alert.information'
          }
        });
      }
    });
  }

  deleteItem(item: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: 'item.deleteItemConfirmation',
        param: { description: item.description }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.confirm) {
        this.itemService.deleteItem(item, item.id).subscribe({
          next: (item) => {
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                message: 'item.itemDeletedSuccess',
                param: { description: item.description },
                title: 'alert.information',
              },
            });
            this.clearForm();
            this.pageChange(this.pageable);
          },
          error: (error) => {
            if (error.status === 409 && error.error.message === 'Item associated to at least an order') {
              this.dialog.open(AlertComponent, {
                disableClose: true,
                data: {
                  message: 'alert.cannotDeleteItem',
                  title: 'alert.information',
                },
              });
            }
          }
        });
      }
    });
  }

  navigate() {
    const isRegisteringOrder = this.route.snapshot.queryParamMap.get('isRegisteringOrder');

    if (isRegisteringOrder) {
      this.router.navigate(['/new-order']);
    } else {
      this.router.navigate(['/orders-dashboard']);
    }
  }

  saveImage(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      if (file.size > maxSizeBytes) {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            title: 'alert.information',
            message: 'item.imageTooLarge'
          }
        });
        this.selectedFile = null;
        this.fileInput.nativeElement.value = '';
        return;
      }

      this.selectedFile = file;
      this.imagePreview = null;

      const reader = new FileReader();
      reader.onload = () => {
        this.editItem.imagePath = reader.result as string;
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  triggerFileInput() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  clearForm() {
    this.isEdit = false;
    this.selectedFile = null;
    this.imagePreview = null;
    this.editItem = {
      id: 0,
      description: '',
      category: { id: 0, category: '' },
      partNumber: '',
      manufacturer: '',
      imagePath: null,
      active: true
    };
    this.itemForm.reset();
    this.itemFormViewChild.resetForm();
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
